import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { SelectOption } from '../../model/select-option';
import { CommonModule } from '@angular/common';
import { ShowErrorComponent } from '../show-error/show-error.component';
import { debounceTime } from 'rxjs';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroChevronDown } from '@ng-icons/heroicons/outline';

@Component({
  selector: 'app-input-select',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ShowErrorComponent,
    NgIcon,
  ],
  templateUrl: './input-select.component.html',
  viewProviders: [provideIcons({ heroChevronDown })],
})
export class InputSelectComponent implements OnInit, OnChanges {
  @Input() label!: string;
  @Input() placeholder = 'Seleccione una opción';
  @Input() control!: FormControl | AbstractControl | null;
  @Input() options: SelectOption[] = [];
  @Input() showError = true;
  @Output() change = new EventEmitter<any>();
  @Input() disabled: boolean = false;
  @ViewChild('container', { read: ElementRef }) containerRef!: ElementRef;

  deployUp: boolean = false;
  private readonly DEPLOY_THRESHOLD = 300;

  searchTerm = new FormControl('');
  filteredOptions: SelectOption[] = [];
  isOpen: boolean = false;
  selectedOptionLabel: string = '';

  private isSelecting: boolean = false;
  private readonly PREVIEW_LIMIT = 5;

  ngOnInit(): void {
    this.updateSelectedLabel(this.formControl.value);
    this.filterOptions(this.searchTerm.value);
    this.searchTerm.valueChanges.pipe(debounceTime(200)).subscribe((term) => {
      this.filterOptions(term);
    });

    this.updateSelectedLabel(this.formControl.value);
    this.formControl.valueChanges.subscribe((value) => {
      this.updateSelectedLabel(value);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['control'] || changes['options']) {
      if (this.formControl && this.options.length > 0) {
        this.updateSelectedLabel(this.formControl.value);
      }
    }
  }

  get formControl(): FormControl {
    return this.control as FormControl;
  }

  get normalizedFieldName(): string {
    if (!this.label) return '';
    const name = this.label.charAt(0).toUpperCase() + this.label.slice(1);
    return name;
  }

  get isRequired(): boolean {
    if (!this.control || !this.control.validator) return false;
    const validator = this.control.validator({} as any);
    return validator?.['required'] === true;
  }

  get showPreview(): boolean {
    return (
      !this.isOpen &&
      !this.searchTerm.value &&
      this.options.length > this.PREVIEW_LIMIT
    );
  }

  filterOptions(term: string | null): void {
    if (!term || term.trim() === '') {
      this.filteredOptions = this.options;
    } else {
      const lowerTerm = term.toLowerCase();
      this.filteredOptions = this.options.filter((opt) =>
        opt.label.toLowerCase().includes(lowerTerm)
      );
    }
  }

  updateSelectedLabel(value: any): void {
    if (value) {
      const selected = this.options.find((opt) => opt.value === value);
      this.selectedOptionLabel = selected ? selected.label : '';
    } else {
      this.selectedOptionLabel = '';
    }
  }

  onBlur(): void {
    setTimeout(() => {
      if (!this.isSelecting) {
        this.isOpen = false;
        console.log('Cerrando dropdown por blur');
      }
      if (!this.formControl.value) {
        this.searchTerm.setValue('');
      }
      this.filterOptions(null);

      this.isSelecting = false;
    }, 150);
  }

  onFocus(): void {
    if (this.disabled) return;
    this.isOpen = true;
    this.filterOptions(this.searchTerm.value);
    this.checkDeployDirection();
  }

  selectOption(option: SelectOption): void {
    if (this.disabled) return;
    this.isSelecting = true;
    this.formControl.setValue(option.value);
    this.change.emit(option.value);
    this.selectedOptionLabel = option.label;
    this.isOpen = false;
    this.searchTerm.setValue('');
  }

  checkDeployDirection(): void {
    if (!this.containerRef) return;
    const rect = this.containerRef.nativeElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    this.deployUp = spaceBelow < this.DEPLOY_THRESHOLD;
  }
}
